import React, { FC, useEffect, useRef, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useForm } from 'react-hook-form'
import moment from 'moment'
import { useAtom } from 'jotai'
import { Button } from '../../ui/Button/Button'
import { Info, LockFill, IcEdit, Calendar } from '../../../assets/icons'
import { Circle } from '../../ui/Card'
import { CircleIcon } from '../../ui/Circle/Circle'
import { Typography } from '../../ui/Typography/Typography'
import {
    FunctionField,
    IUpdateMemberData,
    Member,
} from '../../../models/Member'
import { Position } from '../../../models/Position'
import { getWorkplacesAndPositions } from '../../../api/misc'
import { colors } from '../../../theme/palette'
import { DatePickerComponent } from '../../ui/FormElememts/DatePicker'
import {
    FormInput,
    FormInputNumber,
    FormTextArea,
} from '../FormElements/FormInput'
import { FormControl } from '../../ui/FormElememts/FormControl'
import { FormHeader } from '../../ui/FormElememts/FormHeader'
import { baseButton } from '../../../theme/reuseables'
import { Overlay } from '../../ui/Sidebar'
import { Dialog } from '../../ui/Dialog/Dialog'
import { updateOwnMember } from '../../../api/members/members'
import { DrawerHeaderMemberInfo } from '../FormElements/DrawerHeader'
import { FormMessage } from '../../ui/FormElememts/FormMessage'
import { Workplace } from '../../../models/Workplace'
import { Contact } from '../../../models/Contact'
import { L, L_HTML } from '../../providers/Lokalise'
import { ISelectOptions, Select } from '../../ui/FormElememts/Select'
import { profileAtom } from '../../../api/profile/profile'
import { ImitatorNoAccessWarning } from '../Imitator/ImitatorNoAccessWarning'

interface EditProps {
    member: Member
    onUpdated?: (member: Member) => void
    onOpen?: () => void
}

interface IError {
    hasError: boolean
    message: JSX.Element
}

const useStyles = createUseStyles({
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: 'calc(100% - 100px)',
        paddingBottom: '80px',
    },
    accordionWrapper: {
        marginBottom: '20px',
    },
    select: {
        position: 'relative',
        height: '3.125rem',
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        fontSize: '1.125rem',
        paddingLeft: '.75rem',
        paddingRight: '.75rem',
        cursor: 'pointer',
        color: colors.black,
    },
    button: {
        ...baseButton,
        ...{
            padding: '2.5rem 0 0',
            cursor: 'pointer',
        },
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
})

export type RegistrationFormFields = {
    KontaktPrivat: {
        Fastnet: string
        Mobil: string
        Email: string
    }
    KontaktArbejde: {
        Telefon: string
        Email: string
        OpdaterFunktionFastnet: FunctionField
    }
    Stillingsnummer: string
    Timetal: number
    OevrigeOplysninger: string
    Arbejdsstednummer: number
}

export type CustomErrors = {
    Aendringsdato?: boolean
}

export const EditMemberProfile: FC<EditProps> = ({
    member,
    onUpdated,
    onOpen,
}) => {
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<IError | undefined>(undefined)
    const { register, handleSubmit, formState, reset, setValue } =
        useForm<RegistrationFormFields>()
    const classes = useStyles()
    const [positions, setPositions] = useState<Position[]>([])
    const [selectedDate, _setSelectedDate] = useState(undefined)
    const [isOpen, setIsOpen] = useState(false)
    const [showDialog, setShowDialog] = useState(false)
    const [isValid, setIsValid] = useState(false)
    const [isFetching, setIsFetching] = useState(false)
    const [workPlaces, setWorkplaces] = useState<Workplace[]>([])
    const [customErrors, setCustomErrors] = useState<CustomErrors>({})
    const [selectedPostion, setSelectedPosition] = useState<string | undefined>(
        undefined
    )
    const [workContact, setWorkContact] = useState<Contact | undefined>(
        undefined
    )
    const [showImitatorModal, setShowImitatorModal] = useState(false)
    const [authProfile] = useAtom(profileAtom)

    const [options, setOptions] = useState<ISelectOptions[]>([])

    const [selectedWorkplace, setSelectedWorkplace] = useState<
        string | undefined
    >(undefined)

    const formRef = useRef(null)

    const setWP = (wp: string): void => {
        const found = member.workPlaces.find((wpp) => String(wpp.number) === wp)
        setSelectedWorkplace(wp)

        // Set timetal
        setValue('Timetal', found.hoursPerWeek)

        const wco = member.contactInfoList.find(
            (wc) =>
                wc.contactInfoType === 'Arbejde' &&
                String(wc.workPlacesContact.number) === wp
        )

        if (wco) {
            setValue('KontaktArbejde.Telefon', wco.telephone || undefined)
            setValue('KontaktArbejde.Email', wco.email || undefined)
        } else {
            setValue('KontaktArbejde.Telefon', undefined)
            setValue('KontaktArbejde.Email', undefined)
        }

        if (found && found.positions) {
            setPositions(found.positions)
            const newOptions = found.positions.map((pos) => ({
                label: pos.name,
                value: String(pos.id),
            }))
            setOptions(newOptions)
        }

        if (found) {
            setSelectedPosition(String(found.postionNumber))
            setValue('Stillingsnummer', String(found.postionNumber))
        }
    }

    const getPositions = async (): Promise<void> => {
        const response = await getWorkplacesAndPositions()
        setWorkplaces(response.workPlaces)

        if (member) {
            const found = response.workPlaces.find(
                (wpp) => wpp.number === member.primaryWorkPlace.number
            )

            if (member.workPlaces.length === 1) {
                setSelectedWorkplace(String(member.primaryWorkPlace.number))

                const wco = member.contactInfoList.find(
                    (wc) =>
                        wc.contactInfoType === 'Arbejde' &&
                        String(wc.workPlacesContact.number) ===
                            String(member.primaryWorkPlace.number)
                )

                if (wco) {
                    setValue('KontaktArbejde.Telefon', wco.telephone)
                    setValue('KontaktArbejde.Email', wco.email)
                } else {
                    setValue('KontaktArbejde.Telefon', undefined)
                    setValue('KontaktArbejde.Email', undefined)
                }

                setSelectedPosition(member.primaryWorkPlace.postionNumber)
            }

            if (found && found.positions) {
                setPositions(found.positions)

                const positionssss = found.positions.map((pos) => {
                    return { label: String(pos.name), value: String(pos.id) }
                })

                setOptions(positionssss)
            }
        }
    }

    const init = (): void => {
        if (member.primaryWorkPlace && member.workPlaces.length === 1) {
            setValue('Timetal', member.primaryWorkPlace.hoursPerWeek)
        }
        getPositions()

        if (formRef && formRef.current) {
            const input = formRef.current.querySelector('[name="Timetal"]')
            if (input) {
                input.setAttribute('autofocus', '')
            }
        }
    }

    useEffect(() => {
        init()
    }, [member])

    const resetForm = (): void => {
        if (member?.workPlaces.length > 1) {
            _setSelectedDate(undefined)
            setSelectedPosition(undefined)
            setSelectedWorkplace(undefined)
            setCustomErrors({})
            reset()
        } else if (member?.workPlaces.length === 1) {
            _setSelectedDate(undefined)
            setSelectedPosition(undefined)
            setWP(String(member.primaryWorkPlace.number))
            setCustomErrors({})
            reset()
            setValue('Timetal', member.primaryWorkPlace.hoursPerWeek)
        }
    }

    const { isDirty, dirtyFields, errors } = formState

    const onSubmit = async (data: RegistrationFormFields): Promise<string> => {
        if (authProfile.isImitator) {
            setShowImitatorModal(true)
            return Promise.resolve('OK')
        }

        if (!selectedDate) {
            setCustomErrors({
                Aendringsdato: true,
            })
            return Promise.resolve('OK')
        }

        if (isDirty && selectedDate) {
            setIsLoading(true)
            const workPlace = member.workPlaces.find(
                (wp) => wp.number === +selectedWorkplace
            )

            const fieldsToEdit: IUpdateMemberData = {
                Medlemsident: member.memberId,
                Aendringsdato: moment(selectedDate).format(
                    'YYYY-MM-DD HH:mm:ss'
                ),
            }

            if (selectedWorkplace) {
                fieldsToEdit.Arbejdsstednummer = workPlace.number
            }

            if (selectedWorkplace && selectedPostion) {
                fieldsToEdit.Stillingsnummer = selectedPostion
            }

            if (dirtyFields.Timetal) {
                fieldsToEdit.Timetal = Number(
                    String(data.Timetal).replace(',', '.')
                )
            }

            if (dirtyFields.KontaktPrivat) {
                fieldsToEdit.KontaktPrivat = {}
                if (dirtyFields.KontaktPrivat.Mobil) {
                    fieldsToEdit.KontaktPrivat.Mobil = data.KontaktPrivat.Mobil
                    fieldsToEdit.KontaktPrivat.OpdaterFunktionMobil =
                        data.KontaktPrivat.Mobil.length === 0 ? 'Slet' : 'Ret'

                    if (data.KontaktPrivat.Mobil.length === 0) {
                        delete fieldsToEdit.KontaktPrivat.Mobil
                    }
                }
                if (dirtyFields.KontaktPrivat.Email) {
                    fieldsToEdit.KontaktPrivat.Email = data.KontaktPrivat.Email
                    fieldsToEdit.KontaktPrivat.OpdaterFunktionEmail =
                        data.KontaktPrivat.Email.length === 0 ? 'Slet' : 'Ret'

                    if (data.KontaktPrivat.Email.length === 0) {
                        delete fieldsToEdit.KontaktPrivat.Email
                    }
                }
            }

            if (dirtyFields.KontaktArbejde) {
                fieldsToEdit.KontaktArbejde = {}
                if (dirtyFields.KontaktArbejde.Telefon) {
                    fieldsToEdit.KontaktArbejde.Telefon =
                        data.KontaktArbejde.Telefon
                    fieldsToEdit.KontaktArbejde.OpdaterFunktionTelefon =
                        data.KontaktArbejde.Telefon.length === 0
                            ? 'Slet'
                            : 'Ret'

                    if (data.KontaktArbejde.Telefon.length === 0) {
                        delete fieldsToEdit.KontaktArbejde.Telefon
                    }
                }
                if (dirtyFields.KontaktArbejde.Email) {
                    fieldsToEdit.KontaktArbejde.Email =
                        data.KontaktArbejde.Email

                    fieldsToEdit.KontaktArbejde.OpdaterFunktionEmail =
                        data.KontaktArbejde.Email.length === 0 ? 'Slet' : 'Ret'

                    if (data.KontaktArbejde.Email.length === 0) {
                        delete fieldsToEdit.KontaktArbejde.Email
                    }
                }
            }

            if (dirtyFields.OevrigeOplysninger) {
                fieldsToEdit.OevrigeOplysninger = data.OevrigeOplysninger
            }

            const response: any = await updateOwnMember(fieldsToEdit)

            if (response.error) {
                // setError({
                //     hasError: true,
                //     message: (
                //         <div>
                //             {response.error.ModelState &&
                //                 Object.keys(response.error.ModelState).map(
                //                     (i) => (
                //                         <p style={{ marginBottom: '10px' }}>
                //                             {response.error.ModelState[i]}
                //                         </p>
                //                     )
                //                 )}
                //         </div>
                //     ),
                // })
                // resetForm()
                setIsLoading(false)
            } else if (response) {
                setIsOpen(false)
                setShowDialog(true)
                resetForm()
                setIsLoading(false)

                if (onUpdated) {
                    onUpdated(member)

                    // Reset form
                    resetForm()
                }
            }
        } else if (!selectedDate) {
            setCustomErrors({
                Aendringsdato: true,
            })
        }
        return Promise.resolve('OK')
    }

    const checkIsValid = (): void => {
        if (isDirty && selectedDate) {
            setIsValid(true)
        }
    }

    useEffect(() => {
        checkIsValid()
    })

    return (
        <>
            <Button
                type="action"
                onClick={() => {
                    setIsOpen(true)
                    resetForm()
                    if (onOpen) {
                        onOpen()
                    }
                }}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignContent: 'start',
                    padding: 0,
                    justifyContent: 'left',
                }}
            >
                <Circle marginRight=".5rem">
                    <CircleIcon iconWidth="22px">
                        <IcEdit />
                    </CircleIcon>
                </Circle>
                <Typography tag="h3" size="XS" spacing=".2rem">
                    {L('MINEMEDLEMMER_DETALJE_REDIGERMEDLEM_KNAP')}
                </Typography>
            </Button>
            <Overlay
                isShowing={isOpen}
                hide={() => {
                    resetForm()
                    setIsOpen(false)
                }}
            >
                <DrawerHeaderMemberInfo member={member} />
                <form
                    ref={formRef}
                    onSubmit={handleSubmit(
                        (data) => {
                            if (!selectedDate) {
                                setCustomErrors({
                                    Aendringsdato: true,
                                })
                                return Promise.resolve('OK')
                            }
                            setCustomErrors({})
                            return onSubmit(data)
                        },
                        () => {
                            if (!selectedDate) {
                                setCustomErrors({
                                    Aendringsdato: true,
                                })
                            }
                        }
                    )}
                    className={classes.contentWrapper}
                    noValidate
                >
                    <FormHeader>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                width: '100%',
                            }}
                        >
                            <div style={{ marginBottom: '1rem' }}>
                                <Circle marginRight=".5rem">
                                    <CircleIcon>
                                        <IcEdit />
                                    </CircleIcon>
                                </Circle>
                            </div>
                            <Typography tag="h3" size="M" spacing=".2rem">
                                {L_HTML(
                                    'MINEMEDLEMMER_RETMEDLEMSDETALJE_TITEL'
                                )}
                            </Typography>
                        </div>
                    </FormHeader>
                    {member && (
                        <FormControl>
                            <Select
                                hideBorder
                                useControlled
                                disabled={member.workPlaces.length <= 1}
                                hideArrow={member.workPlaces.length <= 1}
                                defaultValue={selectedWorkplace}
                                value={selectedWorkplace}
                                {...register('Arbejdsstednummer')}
                                placeholder={L(
                                    'MINEMEDLEMMER_RETMEDLEMSDETALJE_ARBEJDSSTED_LABEL'
                                )}
                                onChange={(e) => {
                                    setValue(
                                        'Arbejdsstednummer',
                                        Number(e.target.value)
                                    )
                                    setWP(e.target.value)
                                }}
                                options={member.workPlaces.map((wp) => {
                                    const o = {
                                        label: wp.name,
                                        value: String(wp.number),
                                    }

                                    return o
                                })}
                            />
                            <hr
                                style={{
                                    marginLeft: '-20px',
                                    marginRight: '-20px',
                                    borderColor: colors.borderColorLight,
                                    marginBottom: '2rem',
                                }}
                            />
                        </FormControl>
                    )}
                    <FormControl>
                        {options.length ? (
                            <Select
                                useControlled
                                defaultValue={selectedPostion}
                                {...register('Stillingsnummer')}
                                value={selectedPostion}
                                disabled={!selectedWorkplace}
                                onChange={(e) => {
                                    setValue(
                                        'Stillingsnummer',
                                        String(e.target.value)
                                    )
                                    setSelectedPosition(e.target.value)
                                }}
                                options={options}
                                placeholder={L(
                                    'MINEMEDLEMMER_RETMEDLEMSDETALJE_STILLING_LABEL'
                                )}
                            />
                        ) : null}
                    </FormControl>
                    <FormControl>
                        <FormInputNumber
                            label={`${L('REDIGER-MEDLEM_FORM_TIMETAL_LABEL')}`}
                            name="Timetal"
                            required={!!selectedWorkplace}
                            useFloats
                            register={register}
                            hasError={!!errors?.Timetal?.message}
                            autoComplete="off"
                            type="text"
                            disabled={!selectedWorkplace}
                            rules={
                                selectedWorkplace
                                    ? {
                                          validate: (value) =>
                                              (Number(
                                                  String(value).replace(
                                                      ',',
                                                      '.'
                                                  )
                                              ) >= 0.5 &&
                                                  Number(
                                                      String(value).replace(
                                                          ',',
                                                          '.'
                                                      )
                                                  ) <= 99) ||
                                              L(
                                                  'GENERISK_FORM_TIMETAL_VALIDERINGS_TEKST'
                                              ),
                                      }
                                    : undefined
                            }
                        />
                        <FormMessage
                            text={L('GENERISK_FORM_TIMETAL_VALIDERINGS_TEKST')}
                            error={errors?.Timetal?.message}
                        />
                    </FormControl>
                    <FormControl>
                        <FormInputNumber
                            label={L(
                                'GENERISK_FORM_ARBEDJSPLADS-TELEFON_LABEL'
                            )}
                            name="KontaktArbejde.Telefon"
                            disabled={!selectedWorkplace}
                            required={false}
                            register={register}
                            hasError={
                                !!errors?.KontaktArbejde?.Telefon?.message
                            }
                            rules={{
                                validate: (value) =>
                                    (value && value.length > 7) ||
                                    !value ||
                                    L('GENERISK_FORM_MOBIL_VALIDERINGS_TEKST'),
                            }}
                        />
                        <FormMessage
                            text={L('GENERISK_FORM_MOBIL_VALIDERINGS_TEKST')}
                            error={errors?.KontaktArbejde?.Telefon?.message}
                        />
                    </FormControl>
                    <FormControl>
                        <FormInput
                            label={L('GENERISK_FORM_ARBEJDSMAIL_LABEL')}
                            name="KontaktArbejde.Email"
                            required={false}
                            register={register}
                            disabled={!selectedWorkplace}
                            type="email"
                            hasError={!!errors?.KontaktArbejde?.Email?.message}
                            rules={{
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'indtast en gyldig email',
                                },
                            }}
                        />
                        <FormMessage
                            text="indtast en gyldig email"
                            error={errors?.KontaktArbejde?.Email?.message}
                        />
                    </FormControl>
                    <FormControl>
                        <FormInputNumber
                            label={L('GENERISK_FORM_PRIVAT-MOBIL_LABEL')}
                            name="KontaktPrivat.Mobil"
                            required={false}
                            register={register}
                            defaultValue={member.privateContact?.mobile}
                            hasError={!!errors?.KontaktPrivat?.Mobil?.message}
                            rules={{
                                validate: (value) =>
                                    value.length > 7 ||
                                    !value ||
                                    L('GENERISK_FORM_MOBIL_VALIDERINGS_TEKST'),
                            }}
                        />
                        <FormMessage
                            text={L('GENERISK_FORM_MOBIL_VALIDERINGS_TEKST')}
                            error={errors?.KontaktPrivat?.Mobil?.message}
                        />
                    </FormControl>
                    <FormControl>
                        <FormInput
                            label={L('GENERISK_FORM_PRIVAT-EMAIL_LABEL')}
                            name="KontaktPrivat.Email"
                            required={false}
                            register={register}
                            type="email"
                            defaultValue={member.privateContact?.email || ''}
                            hasError={!!errors?.KontaktPrivat?.Email?.message}
                            rules={{
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'indtast en gyldig email',
                                },
                            }}
                        />
                        <FormMessage
                            text="indtast en gyldig email"
                            error={errors?.KontaktPrivat?.Email?.message}
                        />
                    </FormControl>
                    <FormHeader>
                        <Circle marginRight=".5rem">
                            <CircleIcon iconWidth="20px">
                                <Calendar />
                            </CircleIcon>
                        </Circle>
                        <Typography tag="h3" size="M" spacing=".2rem">
                            {L_HTML('GENERISK_FORM_GAELDER_FRA_TITEL')}
                        </Typography>
                    </FormHeader>
                    <FormControl>
                        <DatePickerComponent
                            label={`${L('GENERISK_FORM_ÆNDRES-PR_LABEL')}`}
                            selected={selectedDate}
                            setSelected={(date) => {
                                _setSelectedDate(date)
                                if (date) {
                                    setCustomErrors({})
                                }
                            }}
                            minDate={moment().subtract(1, 'year').toDate()}
                            maxDate={moment().add(1, 'year').toDate()}
                            hasError={customErrors?.Aendringsdato}
                        />
                        <FormMessage
                            text=""
                            error={
                                customErrors.Aendringsdato
                                    ? 'Ændringsdato påkrævet'
                                    : undefined
                            }
                        />
                    </FormControl>
                    <FormHeader>
                        <Circle marginRight=".5rem">
                            <CircleIcon>
                                <Info />
                            </CircleIcon>
                        </Circle>
                        <Typography tag="h3" size="M" spacing=".2rem">
                            {L_HTML('GENERISK_FORM_OEVRIGE_KOMMENTAR_TITEL')}
                        </Typography>
                    </FormHeader>
                    <FormControl>
                        <FormTextArea
                            label={L('GENERISK_FORM_ØVRIGE-OPLYSNINGER_LABEL')}
                            register={register}
                            name="OevrigeOplysninger"
                            required={false}
                        />
                    </FormControl>
                    <div className={classes.bottom}>
                        <Button
                            onClick={() => {
                                setIsOpen(false)
                                resetForm()
                            }}
                            type="secondary"
                            size="large"
                            disabled={isLoading}
                        >
                            {L('MINEMEDLEMMER_RETMEDLEMSDETALJE_LUK_KNAP')}
                        </Button>
                        <Button
                            buttonType="submit"
                            type="primary"
                            size="large"
                            disabled={!isDirty}
                            loading={isLoading}
                        >
                            {L('MINEMEDLEMMER_RETMEDLEMSDETALJE_GEM_KNAP')}
                        </Button>
                    </div>

                    <div />
                </form>
            </Overlay>
            {showDialog ? (
                <Dialog
                    trigger={null}
                    defaultOpen
                    hideClose
                    icon={<LockFill width="46px" />}
                >
                    <Typography
                        style={{ marginBottom: '30px' }}
                        size="L"
                        align="center"
                    >
                        {L_HTML(
                            'MINEMEDLEMMER_RETMEDLEMSDETALJE_INDSENDT-FEEDBACK_TITEL'
                        )}
                    </Typography>
                    <Typography
                        tag="p"
                        size="S"
                        style={{ marginBottom: '30px' }}
                        align="center"
                    >
                        {L_HTML(
                            'MINEMEDLEMMER_RETMEDLEMSDETALJE_INDSENDT-FEEDBACK_TEKST'
                        )}
                    </Typography>
                    <Button size="xlarge" onClick={() => setShowDialog(false)}>
                        {L_HTML(
                            'MINEMEDLEMMER_RETMEDLEMSDETALJE_INDSENDT-FEEDBACK_OK_KNAP'
                        )}
                    </Button>
                </Dialog>
            ) : null}
            <ImitatorNoAccessWarning
                show={showImitatorModal}
                onClose={() => setShowImitatorModal(false)}
            />
            {/* {error && error.hasError ? (
                <Dialog
                    trigger={null}
                    defaultOpen
                    hideClose
                    icon={<LockFill width="46px" />}
                >
                    <Typography
                        style={{ marginBottom: '30px' }}
                        size="L"
                        align="center"
                    >
                        Fejl
                    </Typography>
                    <Typography
                        tag="p"
                        size="S"
                        style={{ marginBottom: '30px' }}
                        align="center"
                    >
                        {error.message}
                    </Typography>
                    <Button size="xlarge" onClick={() => setError(undefined)}>
                        {L_HTML(
                            'MINEMEDLEMMER_RETMEDLEMSDETALJE_INDSENDT-FEEDBACK_OK_KNAP'
                        )}
                    </Button>
                </Dialog>
            ) : null} */}
        </>
    )
}
